// DropSender — serves a set of files to every authenticated peer (FilePizza's "uploader is
// the server" model). Link mode: the manifest goes to each peer once authenticated. Nearby
// mode: files are offered to one chosen peer, who accepts or declines.
//
// Events: 'change' (any receiver state changed).

import type { Mesh } from '../core/mesh';
import { randomHex } from '../core/ids';
import { parseCtl, safeName, type FileMeta } from './protocol';
import { fileReader, sendFile } from './transfer';

export type ReceiverStatus = 'connecting' | 'offered' | 'ready' | 'receiving' | 'done' | 'declined' | 'refused' | 'gone';

export type ReceiverState = {
  peer: string;
  name: string;
  status: ReceiverStatus;
  /** Bytes confirmed by the receiver, per file. */
  acked: Map<string, number>;
  done: Set<string>;
};

export class DropSender extends EventTarget {
  readonly metas: FileMeta[];
  readonly totalSize: number;
  readonly receivers = new Map<string, ReceiverState>();
  readonly #files = new Map<string, File>();
  readonly #active = new Map<string, AbortController>();
  #attempts = 0;
  #listeners: [string, EventListener][] = [];

  constructor(
    readonly mesh: Mesh,
    files: File[],
    readonly opts: { from: string; mode: 'link' | 'nearby' },
  ) {
    super();
    this.metas = files.map((file) => {
      const id = randomHex(8);
      this.#files.set(id, file);
      return { id, name: safeName(file.name), size: file.size, type: file.type || 'application/octet-stream' };
    });
    this.totalSize = this.metas.reduce((n, f) => n + f.size, 0);
  }

  start(): void {
    // Link mode serves every peer of the room; nearby mode only the peer the files were offered to.
    const link = this.opts.mode === 'link';
    this.#on('peer', ({ peer }) => link && this.#state(peer));
    this.#on('authed', ({ peer }) => {
      if (!link) return;
      const r = this.#state(peer);
      this.mesh.send(peer, { t: 'manifest', files: this.metas, from: this.opts.from });
      if (r.status === 'connecting') r.status = 'ready';
      this.#changed();
    });
    this.#on('auth-failed', ({ peer }) => {
      if (!link) return;
      this.#state(peer).status = 'refused';
      this.#changed();
    });
    this.#on('down', ({ peer }) => this.#abortPeer(peer));
    this.#on('gone', ({ peer }) => {
      this.#abortPeer(peer);
      const r = this.receivers.get(peer);
      if (r && r.status !== 'done') r.status = 'gone';
      this.#changed();
    });
    this.#on('ctl', ({ peer, msg }) => this.#onCtl(peer as string, msg));
  }

  /** Nearby mode: proposes the files to one peer. */
  offer(peer: string): void {
    const r = this.#state(peer);
    r.status = 'offered';
    this.mesh.send(peer, { t: 'offer', files: this.metas, from: this.opts.from });
    this.#changed();
  }

  /** Stops serving and tells every receiver that has not finished. */
  stop(): void {
    for (const r of this.receivers.values()) if (r.status !== 'done') this.mesh.send(r.peer, { t: 'cancel' });
    this.dispose();
  }

  /** Stops listening and serving, silently. */
  dispose(): void {
    for (const ac of this.#active.values()) ac.abort();
    this.#active.clear();
    for (const [type, fn] of this.#listeners) this.mesh.removeEventListener(type, fn);
    this.#listeners = [];
  }

  bytesFor(r: ReceiverState): number {
    let n = 0;
    for (const v of r.acked.values()) n += v;
    return n;
  }

  #on(type: string, fn: (detail: Record<string, any>) => void): void {
    const listener: EventListener = (e) => fn((e as CustomEvent).detail);
    this.mesh.addEventListener(type, listener);
    this.#listeners.push([type, listener]);
  }

  #state(peer: string): ReceiverState {
    let r = this.receivers.get(peer);
    if (!r) {
      r = { peer, name: `appareil ${this.receivers.size + 1}`, status: 'connecting', acked: new Map(), done: new Set() };
      this.receivers.set(peer, r);
      this.#changed();
    }
    return r;
  }

  #changed(): void {
    this.dispatchEvent(new Event('change'));
  }

  #abortPeer(peer: string): void {
    for (const [key, ac] of this.#active) {
      if (key.startsWith(`${peer}:`)) {
        ac.abort();
        this.#active.delete(key);
      }
    }
  }

  #onCtl(peer: string, raw: unknown): void {
    const msg = parseCtl(raw);
    if (!msg || (this.opts.mode === 'nearby' && !this.receivers.has(peer))) return;
    const r = this.#state(peer);
    switch (msg.t) {
      case 'hello':
        r.name = msg.name;
        break;
      case 'accept':
        r.status = 'ready';
        break;
      case 'decline':
        r.status = 'declined';
        break;
      case 'get':
        void this.#serve(r, msg.file, msg.offset);
        break;
      case 'done':
        r.done.add(msg.file);
        r.acked.set(msg.file, this.#files.get(msg.file)?.size ?? 0);
        if (r.done.size === this.metas.length) r.status = 'done';
        break;
      case 'cancel':
        if (msg.file) this.#active.get(`${peer}:${msg.file}`)?.abort();
        else this.#abortPeer(peer);
        break;
      default:
        return;
    }
    this.#changed();
  }

  async #serve(r: ReceiverState, fileId: string, offset: number): Promise<void> {
    const file = this.#files.get(fileId);
    // Unknown ids may belong to another sender sharing this mesh (nearby mode): ignore them.
    if (!file || offset > file.size || r.status === 'declined') return;
    const key = `${r.peer}:${fileId}`;
    this.#active.get(key)?.abort();
    const ac = new AbortController();
    this.#active.set(key, ac);
    r.status = 'receiving';
    r.acked.set(fileId, offset);
    this.#changed();
    try {
      const dc = await this.mesh.openChannel(r.peer, `f:${fileId}:${++this.#attempts}`);
      await sendFile(dc, fileReader(file), file.size, offset, {
        signal: ac.signal,
        onAck: (acked) => {
          r.acked.set(fileId, acked);
          this.#changed();
        },
      });
    } catch {
      // Channel lost or aborted: the receiver asks again from its own offset when it can.
    } finally {
      if (this.#active.get(key) === ac) this.#active.delete(key);
    }
  }
}
